<template>
    <app-layout>
        <base-header
            :back-url="route('stores.profit-and-loss.index', store.id)"
            :shadow="false"
            border
            border-bg-from="from-orange-500"
            border-bg-to="to-red-500"
        >
            <div class="flex items-center justify-between w-full">
                <div>
                    <h4 class="text-xl font-medium">
                        {{ $t('general.profit_and_loss') }} {{ $moment(month + '-' + year, 'M-Y').format('MMMM YYYY') }}
                    </h4>
                    <div class="flex flex-col">
                        <span v-if="!auth.user.isAdmin || auth.user.stores.length === 1">
                            {{ store.name }}
                        </span>

                        <store-navigation
                            v-else
                            :store="store"
                            @navigate="$inertia.visit(route('stores.profit-and-loss.edit', [$event.id, year, month]))"
                        />
                    </div>
                </div>
            </div>
        </base-header>

        <data-table
            :auth="auth"
            :collection="collection"
            :delete-modal-title="$t('form.delete_confirm')"
            has-comparison-columns
        >
            <template v-slot:column-actions="slot">
                <copy-values :column="slot.column" :collection="collection"/>
            </template>
        </data-table>
    </app-layout>
</template>

<script>
import DataTable from '@/Components/Storemanager/DataTable';
import Projection from '@/Models/Projection';
import BudgetEntry from '@/Models/BudgetEntry';
import DataColumnCollection from '@/Components/Storemanager/Entities/DataColumnCollection';
import DataColumn from '@/Components/Storemanager/Entities/DataColumn';
import ProfitAndLoss from '@/Models/ProfitAndLoss';
import Actual from '@/Models/Actual';
import Store from '@/Models/Store';
import Helper from '@/Utilities/Helper';
import Extrapolation from '@/Models/Extrapolation';
import StoreNavigation from '@/Pages/Stores/Components/StoreNavigation';
import CopyValues from '@/Pages/Stores/ProfitAndLoss/Components/CopyValues';

export default {
    components: {CopyValues, StoreNavigation, DataTable},

    props: {
        store: {
            type: Store,
            required: true,
        },
        year: {
            type: Number,
            required: true,
        },
        month: {
            type: Number,
            required: true,
        },
        budgetEntry: {
            // eslint-disable-next-line vue/require-prop-type-constructor
            type: BudgetEntry | null,
            default: null,
        },
        projection: {
            // eslint-disable-next-line vue/require-prop-type-constructor
            type: Projection | null,
            default: null,
        },
        extrapolations: {
            type: Array,
            default: null,
        },
        profitAndLoss: {
            type: ProfitAndLoss,
            default: null,
        },
        actual: {
            type: Actual,
            default: null,
        },
    },

    metaInfo() {
        return {
            title: this.$t('general.profit_and_loss') + ' - ' +
                moment(this.year + '-' + this.month + '-' + '10', 'YYYY-MM-DD').format('MMMM YYYY'),
        };
    },

    data: function() {
        return {
            collection: new DataColumnCollection(),
        };
    },

    created() {
        let columns = [];
        let date = moment.utc( this.year + '-' + this.month + '-10', 'YYYY-MM-DD').startOf('month');

        // Default Entities
        let budgetEntry = new BudgetEntry({store_id: this.store.id, date: date.format()});
        let projection = new Projection({store_id: this.store.id, date: date.format()});
        let profitAndLoss = new ProfitAndLoss({store_id: this.store.id, date: date.format()});
        let actual = new Actual({store_id: this.store.id, date: date.format()});

        columns.push(new DataColumn(this.budgetEntry ? this.budgetEntry : budgetEntry, true));
        columns.push(new DataColumn(this.projection ? this.projection : projection));

        let dates = Helper.getExtrapolationDatesForMonth(this.year, this.month);

        dates.forEach(date => {
            let extrapolation = new Extrapolation({store_id: this.store.id, date: date.format()});

            if (Array.isArray(this.extrapolations) && this.extrapolations.length > 0) {
                let foundExtrapolation = this.extrapolations.find(extrapolation => date.isSame(extrapolation.date));

                if (foundExtrapolation) {
                    extrapolation = foundExtrapolation;
                }
            }

            columns.push(new DataColumn(extrapolation));
        });

        columns.push(new DataColumn(this.profitAndLoss ? this.profitAndLoss : profitAndLoss));
        columns.push(new DataColumn(this.actual ? this.actual : actual, true));

        this.collection.columns = columns;
    },
};
</script>
